import React, {
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import {
  GridApi,
  ICellRendererParams,
  RowEditingStoppedEvent,
  RowEvent,
  RowNode,
  AllCommunityModule,
  ModuleRegistry,
} from 'ag-grid-community';
import type { ColDef } from 'ag-grid-community';
import classnames from 'classnames';
import styles from './GridEditor.module.css';
import GridActions from './GridActions';
import CellEditor from './CellEditor';

ModuleRegistry.registerModules([AllCommunityModule]);

type FrameworkComponents = {
  [key: string]: (props: RowNode) => ReactElement | null;
};

interface GridEditorProps {
  columns: ColDef[];
  data: GridEditorRecord[];
  onGridReady?: (gridApi: GridApi) => void;
  className?: string;
  editingRowIndex?: number | null;
  onRowSaveClick?: (row: RowEditingStoppedEvent) => void;
  onEditClick?: (row: RowEvent<never>) => void;
  onDeleteClick?: (cell: ICellRendererParams) => void;
  onInfoClick?: (cell: ICellRendererParams) => void;
  editAriaLabel?: string;
  deleteAriaLabel?: string;
  updateAriaLabel?: string;
  customComponents?: FrameworkComponents;
  customProps?: AgGridReactProps;
}

export function GridEditor({
  className,
  columns,
  data,
  editingRowIndex,
  onRowSaveClick,
  onEditClick,
  onDeleteClick,
  onInfoClick,
  onGridReady: onGridReadyProp,
  editAriaLabel,
  deleteAriaLabel,
  updateAriaLabel,
  customComponents,
  customProps,
}: GridEditorProps): ReactElement {
  const [gridApi, setGridApi] = useState<GridApi>();
  const gridRef = useRef(null);

  const defaultColDef = useMemo(() => {
    return {
      editable: !!onEditClick,
      resizable: true,
      sortable: true,
    };
  }, [onEditClick]);

  const columnDefs = useMemo(() => {
    const colDefs: ColDef[] = columns.map((column) => ({
      cellEditor: column.cellEditor || 'cellEditor',
      suppressKeyboardEvent: () => true,
      ...column,
    }));

    if (!!onEditClick || !!onDeleteClick || !!onInfoClick) {
      colDefs.push({
        cellRenderer: 'gridActions',
        cellRendererParams: {
          deleteAriaLabel,
          editAriaLabel,
          editingRowIndex,
          gridApi,
          onDeleteClick,
          onEditClick,
          onInfoClick,
          updateAriaLabel,
        },
        editable: false,
        field: '',
        headerName: '',
        sortable: false,
        suppressNavigable: true,
        width: 100,
      });
    }

    return colDefs;
  }, [
    columns,
    deleteAriaLabel,
    editAriaLabel,
    editingRowIndex,
    gridApi,
    onDeleteClick,
    onEditClick,
    onInfoClick,
    updateAriaLabel,
  ]);

  const onGridReady = useCallback(
    (gridData) => {
      gridData.api.setGridOption('domLayout', 'autoHeight');
      setGridApi(gridData.api);
      if (onGridReadyProp) {
        onGridReadyProp(gridData.api);
      }
    },
    [onGridReadyProp],
  );

  const frameworkComponents = {
    cellEditor: CellEditor,
    gridActions: GridActions,
    ...customComponents,
  };

  return (
    <div className={classnames(styles.grid, className)}>
      <AgGridReact
        theme={'legacy'}
        defaultColDef={defaultColDef}
        editType="fullRow"
        components={frameworkComponents}
        headerHeight={45}
        rowHeight={45}
        icons={{
          sortAscending: '<span class="icon-dropdown"></span>',
          sortDescending:
            '<span class="icon-dropdown icon-dropdown-reverse"></span>',
        }}
        suppressClickEdit={true}
        onGridReady={onGridReady}
        onRowEditingStopped={onRowSaveClick}
        ref={gridRef}
        rowData={data}
        columnDefs={columnDefs}
        {...customProps}
      />
    </div>
  );
}
